import React, { useState, useEffect } from "react";
import { View, TouchableOpacity, ScrollView, Linking } from "react-native";
import { KeyboardAwareScrollView } from "@/components/ui/KeyboardAvoidingView";
import { Controller } from "react-hook-form";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/Button";
import { AppTextInput } from "@/components/ui/TextInput";
import { useResetPasswordForm } from "@/features/auth/hooks/useResetPasswordForm";
import { toast } from "@/lib/toast";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { Image } from "@/components/ui/Image";
import { PageFooter } from "@/components/ui/PageFooter";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import { SUPPORT_EMAIL } from "@/constants";

const HERO_IMAGE = require("@assets/images/login-background-image.png");

// ─── Form content (shared mobile + web) ──────────────────────────────────────

function ResetPasswordForm() {
  const { t } = useI18n();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { form, isLoading, error, handleSubmit } = useResetPasswordForm();

  useEffect(() => {
    if (!error) return;

    toast.error({
      message: t(error as TranslationKeys),
    });
  }, [error, t]);

  return (
    <View className="items-start w-full">
      <View className="w-full items-start">
        <AppText className="text-3xl text-dark-text-50 mb-2" weight="bold">
          {t("auth.resetPassword.title")}
        </AppText>
        <AppText className="text-base text-dark-text-15 mb-8 text-start">
          {t("auth.resetPassword.subtitle")}
        </AppText>
      </View>

      <Controller
        control={form.control}
        name="new_password"
        render={({ field: { value, onChange, onBlur }, fieldState: { error: fieldError } }) => (
          <AppTextInput
            required
            label="auth.resetPassword.newPasswordLabel"
            placeholder="auth.resetPassword.newPasswordPlaceholder"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            secureTextEntry={!showPassword}
            error={fieldError?.message as TranslationKeys | undefined}
            trailingIcon={
              <TouchableOpacity
                onPress={() => setShowPassword((v) => !v)}
                accessibilityRole="button"
              >
                <Icon
                  as={Feather}
                  name={showPassword ? "eye-off" : "eye"}
                  size={20}
                  className="text-supporting-text"
                />
              </TouchableOpacity>
            }
          />
        )}
      />

      <AppText className="text-xs text-dark-text-15 -mt-2 mb-4">
        {t("auth.register.passwordHint")}
      </AppText>

      <Controller
        control={form.control}
        name="confirm_new_password"
        render={({ field: { value, onChange, onBlur }, fieldState: { error: fieldError } }) => (
          <AppTextInput
            required
            label="auth.resetPassword.confirmPasswordLabel"
            placeholder="auth.resetPassword.confirmPasswordPlaceholder"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            secureTextEntry={!showConfirmPassword}
            error={fieldError?.message as TranslationKeys | undefined}
            trailingIcon={
              <TouchableOpacity
                onPress={() => setShowConfirmPassword((v) => !v)}
                accessibilityRole="button"
              >
                <Icon
                  as={Feather}
                  name={showConfirmPassword ? "eye-off" : "eye"}
                  size={20}
                  className="text-supporting-text"
                />
              </TouchableOpacity>
            }
          />
        )}
      />

      <Button
        className="w-full mt-3"
        label="auth.resetPassword.submit"
        onPress={handleSubmit}
        loading={isLoading}
        variant="primary"
      />

      {/* Contact support */}
      <View className="flex-row items-center self-center mt-6">
        <AppText className="text-sm text-dark-text-15">{t("auth.resetPassword.needHelp")} </AppText>
        <TouchableOpacity
          onPress={() => Linking.openURL(`mailto:${SUPPORT_EMAIL}`)}
          accessibilityRole="link"
        >
          <AppText weight="medium" className="text-sm text-blue-50 underline">
            {t("auth.resetPassword.contactSupport")}
          </AppText>
        </TouchableOpacity>
      </View>
    </View>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function ResetPasswordScreen() {
  const insets = useSafeAreaInsets();
  const isWide = useIsWideScreen();

  // ── Wide-screen web layout (≥768px): split image + form ────────────────────
  if (isWide) {
    return (
      <View className="flex-1 bg-white min-h-full">
        <View className="flex-1 flex-row">
          {/* Left image panel */}
          <Image
            source={HERO_IMAGE}
            className="flex-1 bg-blue-90 max-w-xs lg:max-w-md"
            contentFit="cover"
          />

          {/* Right scrollable form */}
          <ScrollView
            className="flex-1 bg-white"
            contentContainerStyle={{ flexGrow: 1 }}
            showsVerticalScrollIndicator={false}
          >
            <View className="flex-1 items-center justify-start py-12 px-8">
              <View className="max-w-md w-full">
                <ResetPasswordForm />
              </View>
            </View>
            <PageFooter />
          </ScrollView>
        </View>
      </View>
    );
  }

  // ── Mobile layout ────────────────────────────────────────────────────────
  return (
    <KeyboardAwareScrollView
      contentContainerStyle={{ flexGrow: 1, paddingBottom: Math.max(insets.bottom, 24) }}
      className="bg-white py-4 tablet:py-page-y"
    >
      <View className="self-center w-full tablet:max-w-3xl px-page-x">
        <ResetPasswordForm />
      </View>
    </KeyboardAwareScrollView>
  );
}
